import {IPrdictorStore} from "apps/BUDWEISERPOTM/modules/types/store";
import {createSelector} from "reselect";
import {
	chain,
	filter,
	find,
	findIndex,
	findLast,
	intersection,
	keyBy,
	last,
	orderBy,
	take,
} from "lodash";
import {GameStatusEnum, OrderEnum} from "apps/BUDWEISERPOTM/modules/enums";
import {RequestState} from "modules";
import {DateTime, Interval} from "luxon";
import {IPredictorGame} from "apps/BUDWEISERPOTM/modules/actions";
import {COUNTRIES_WITHOUT_BEER} from "apps/BUDWEISERPOTM/modules/constants";

export const getGamesStore = (state: IPrdictorStore) => state.games;

export const getGames = createSelector(getGamesStore, (store) => store.games || []);
export const getGamesById = createSelector(getGamesStore, (store) => keyBy(store.games, "id"));

export const getGameById = createSelector(getGames, (games) => (gameID: number | null) => {
	if (!gameID) {
		return;
	}
	return games.find((game) => game.id === gameID);
});

const getGamesForDate = ({
	nextScheduled,
	lastCompleted,
}: {
	nextScheduled: IPredictorGame[];
	lastCompleted: IPredictorGame;
}) => {
	const [firstScheduled, secondScheduled] = nextScheduled;
	const startDate = firstScheduled && DateTime.fromISO(firstScheduled.date);
	const startAfterNextDate = secondScheduled && DateTime.fromISO(secondScheduled.date);

	const nextGames =
		startDate?.toMillis() === startAfterNextDate?.toMillis()
			? [firstScheduled, secondScheduled]
			: [firstScheduled];

	// If last match was yesterday, show new match
	const nowInDoha = DateTime.local().setZone("AST");
	const lastCompletedMatchTimeInDoha = DateTime.fromISO(lastCompleted.date).setZone("AST");
	if (nowInDoha.startOf("day") > lastCompletedMatchTimeInDoha.startOf("day")) {
		return nextGames;
	}

	// Show completed game if next scheduled starts more than in 15 mins

	const diff = Interval.fromDateTimes(DateTime.now(), startDate);
	const mins = diff.isValid ? diff.length("minutes") : 0;

	return mins > 15 ? [lastCompleted] : nextGames;
};

export const getLiveVoteGames = createSelector(getGames, (games) =>
	games.filter((game) => game.status === GameStatusEnum.VoteLive)
);

export const getActualGames = createSelector(getGames, (games) => {
	const playingGames = filter(games, (game) =>
		[GameStatusEnum.Live, GameStatusEnum.VoteLive].includes(game.status)
	);
	if (playingGames.length > 0) {
		return playingGames;
	}
	const lastCompleted = findLast(games, (game) => game.status === GameStatusEnum.Complete);
	if (!lastCompleted) {
		return [games[0]];
	}
	const nextScheduled = filter(games, (game) => game.status === GameStatusEnum.PreGame);
	if (!nextScheduled.length) {
		return [lastCompleted];
	}

	return getGamesForDate({nextScheduled, lastCompleted});
});

export const getVoteLiveGame = createSelector(getGames, (games) =>
	find(games, ({status}) => status === GameStatusEnum.VoteLive)
);

export const getMatchForTimer = createSelector(getGames, getActualGames, (games, actualGames) => {
	const actualGame = actualGames.find((game) => game.status !== GameStatusEnum.Complete);
	if (actualGame) {
		return actualGame;
	}
	const lastActualGame = last(actualGames);
	const actualGameIndex = findIndex(games, (game) => game.id === lastActualGame!.id);
	const nextGame = games[actualGameIndex + 1];
	return nextGame || lastActualGame;
});

export const getTimeWhenVoteStart = createSelector(getMatchForTimer, (match) => {
	const start = DateTime.fromISO(match.date);
	const now = DateTime.now();

	const mins_since_start = now.diff(start).toObject() || 0;
	const mills = mins_since_start?.milliseconds || 0;
	const diff = mills / 60000;

	const minute = match?.minute || 0;
	const isFirstPeriod = diff > 0 && diff < 65;
	const mins = isFirstPeriod ? 75 - diff : 60 - minute;
	const startOrNow = mills < 0 ? start : now;

	return startOrNow.plus({minute: mins}).toISO();
});

export const getIsVoteOpenSoon = createSelector(getGamesById, (games) => (matchId: number) => {
	const game = games[matchId];
	return game.status === "live" && (game.minute || 0) < 60;
});

export const isGamesLoaded = createSelector(
	getGamesStore,
	(store) => store.requestState === RequestState.Received
);

export const getNotCompletedGames = createSelector(getGames, (games) =>
	games.filter(({status}) => status !== GameStatusEnum.Complete)
);

const HOW_MUCH_GAMES_FOR_LANDING = 4;
export const getNonCompletedGamesForLanding = createSelector(getNotCompletedGames, (games) =>
	take(orderBy(games, ["date"], [OrderEnum.Asc]), HOW_MUCH_GAMES_FOR_LANDING)
);

export const getCompletedGames = createSelector(getGames, (games) => {
	return orderBy(
		games.filter(({status}) => status === GameStatusEnum.Complete),
		["date"],
		[OrderEnum.Desc]
	);
});

export const getCompletedGamesSelectOptions = createSelector(getCompletedGames, (games) =>
	chain(games)
		.map((game: IPredictorGame) => ({
			roundId: game.roundId,
			roundName: game.roundName,
		}))
		.uniqBy("roundId")
		.value()
);

export const isFeaturedGameWithSponsor = createSelector(getActualGames, (games) => {
	if (!games.length) {
		return true;
	}
	const gameFiltered = games.find(
		(game) =>
			intersection(COUNTRIES_WITHOUT_BEER, [game.homeSquadFeedId, game.awaySquadFeedId])
				.length === 0
	);

	return Boolean(gameFiltered);
});
